import React, { useEffect } from "react";
import { imageUrl } from "../lib/images";

/**
 * Reusable Lightbox modal with next/prev navigation, keyboard support (Arrow keys, Esc),
 * swipe/click indicators, and counter.
 *
 * Props:
 * - images: Array<{ src: string, title?: string, category?: string } | string>
 * - currentIndex: number | null
 * - onClose: () => void
 * - onNavigate: (index: number) => void
 */
export default function LightboxModal({ images = [], currentIndex, onClose, onNavigate }) {
  const isOpen = currentIndex !== null && currentIndex >= 0 && Boolean(images && images.length > 0);
  const currentItem = isOpen ? images[currentIndex] : null;
  const currentSrc = currentItem ? (typeof currentItem === "string" ? currentItem : currentItem.src) : "";
  const currentTitle = typeof currentItem === "object" && currentItem ? currentItem.title : "";
  const currentCategory = typeof currentItem === "object" && currentItem ? currentItem.category : "";
  const hasMultiple = images.length > 1;

  const handlePrev = (e) => {
    e?.stopPropagation?.();
    if (!hasMultiple) return;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    onNavigate(prevIndex);
  };

  const handleNext = (e) => {
    e?.stopPropagation?.();
    if (!hasMultiple) return;
    const nextIndex = (currentIndex + 1) % images.length;
    onNavigate(nextIndex);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow || "";
    };
  }, [isOpen, currentIndex, images.length]);

  if (!isOpen || !currentItem) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 select-none"
      onClick={onClose}
    >
      {/* Top bar with counter and close button */}
      <div
        className="absolute top-4 inset-x-4 sm:inset-x-8 flex items-center justify-between z-20 pointer-events-none"
      >
        <div className="bg-slate-900/80 backdrop-blur-md text-slate-200 border border-white/10 px-3.5 py-1.5 rounded-full text-xs font-semibold pointer-events-auto">
          {currentIndex + 1} / {images.length}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="pointer-events-auto w-10 h-10 rounded-full bg-slate-900/80 backdrop-blur-md hover:bg-slate-800 text-white hover:text-[#C2CCDF] border border-white/10 flex items-center justify-center text-lg transition-colors cursor-pointer"
          aria-label="Luk billede (Esc)"
          title="Luk (Esc)"
        >
          <i className="fa-sharp fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>

      {/* Prev button */}
      {hasMultiple && (
        <button
          type="button"
          onClick={handlePrev}
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-900/70 hover:bg-[#004FA4] text-white flex items-center justify-center text-lg sm:text-xl border border-white/15 backdrop-blur-md shadow-2xl transition-all cursor-pointer hover:scale-105"
          aria-label="Forrige billede (Venstre piltast)"
          title="Forrige (Venstre pil)"
        >
          <i className="fa-sharp fa-solid fa-chevron-left" aria-hidden="true"></i>
        </button>
      )}

      {/* Next button */}
      {hasMultiple && (
        <button
          type="button"
          onClick={handleNext}
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-900/70 hover:bg-[#004FA4] text-white flex items-center justify-center text-lg sm:text-xl border border-white/15 backdrop-blur-md shadow-2xl transition-all cursor-pointer hover:scale-105"
          aria-label="Næste billede (Højre piltast)"
          title="Næste (Højre pil)"
        >
          <i className="fa-sharp fa-solid fa-chevron-right" aria-hidden="true"></i>
        </button>
      )}

      {/* Main Image and Caption Container */}
      <div
        className="relative max-w-5xl max-h-[92vh] flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden rounded-xl bg-black/40 flex items-center justify-center shadow-2xl">
          <img
            key={currentSrc}
            src={imageUrl(currentSrc, "width=1600,quality=90,format=auto")}
            alt={currentTitle || "Hovendals"}
            className="max-h-[78vh] sm:max-h-[82vh] w-auto max-w-full rounded-xl object-contain animate-fadeIn"
          />
        </div>

        {(currentCategory || currentTitle) && (
          <div className="text-white text-center mt-3 max-w-2xl px-4">
            {currentCategory && (
              <div className="text-xs font-bold uppercase tracking-wider text-[#C2CCDF] mb-0.5">
                {currentCategory}
              </div>
            )}
            {currentTitle && (
              <div className="text-sm sm:text-base font-semibold text-slate-100">
                {currentTitle}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
